import { Divider, Grid } from '@mui/material';
import React, { useEffect } from 'react';
import { Path } from 'react-hook-form/dist/types';
import { InputStatus } from 'src/components/input/Input.interface';
import { InputTextLabel } from 'src/components/input/Input.styled';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  RichTextEditor,
  RichTextToolbarOptions,
  SelectDropdownWidget,
  SwitchButtonWidget,
  TextAlignButtonsWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { isDy } from 'src/features/components/editComponentPage/EditComponentPage.helper';
import { HeaderLayout } from 'src/features/components/editComponentPage/forms/FormSelector.interface';
import { HeaderTextStylingOptions } from 'src/features/components/editComponentPage/forms/modules/headerTextStylingAccordion/HeaderTextStylingAccordion.interface';
import { useGetProjectConfig } from 'src/hooks';
import { useEvent } from 'src/hooks/useEvent';
import { DyProductSource, IGcfComponentConfig } from 'src/interfaces';
import { GcfMockData } from 'src/utils/gcf-helper';
import { lang } from 'src/utils/language';

import { DyHeaderOptions, HeaderWidgetProps } from './HeaderWidget.interface';
import {
  HeaderWidgetButton,
  HeaderWidgetButtonGroup,
} from './HeaderWidget.styled';
import {
  convertSpacesToHTMLFormat,
  getErrorKey,
  getHeader,
  getHeaderKey,
  getHeaderKeyString,
} from './HeaderWidget.util';

export const HeaderWidget = ({
  componentConfig,
  handleOnChange,
  errors,
  theme,
  hidden,
}: HeaderWidgetProps) => {
  const [headerType, setHeaderType] = React.useState<DyHeaderOptions>(
    DyHeaderOptions.UNKNOWN,
  );

  const hideToolbarItems: RichTextToolbarOptions[] = Object.values(
    RichTextToolbarOptions,
  ).filter(
    (option) =>
      option !== RichTextToolbarOptions.SUPER_SCRIPT &&
      option !== RichTextToolbarOptions.SPECIAL_CHAR_SELECT &&
      option !== RichTextToolbarOptions.SIZE,
  );

  const { triggerEvent: triggerDySuggestionEvent } = useEvent(
    GcfMockData.DY_SUGGESTIONS_OPEN,
    {},
  );
  const { triggerEvent: triggerDyRecommendationEvent } = useEvent(
    GcfMockData.DY_RECOMMENDATIONS_UPDATE,
    {},
  );

  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  const handleHeaderCustomerTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newHeaderType: string,
  ) => {
    if (!newHeaderType) return;

    const isUnknown = newHeaderType === DyHeaderOptions.UNKNOWN;

    // Main logic
    const isRecommendation =
      componentConfig.productsSource === DyProductSource.DYR;
    const headerOption = isUnknown
      ? DyHeaderOptions.UNKNOWN
      : DyHeaderOptions.KNOWN;

    // Set the header type and trigger event

    isRecommendation
      ? triggerDyRecommendationEvent({ isCustomerKnown: !isUnknown })
      : triggerDySuggestionEvent({ isCustomerKnown: !isUnknown });
    setHeaderType(headerOption);
  };

  const headerKey = React.useMemo(
    () => getHeaderKey(componentConfig, headerType),
    [componentConfig, headerType],
  );

  const headerKeyString = (key?: string) =>
    getHeaderKeyString(
      componentConfig,
      headerType,
      key,
    ) as Path<IGcfComponentConfig>;
  const errorKey = getErrorKey(componentConfig, headerType, errors);
  const getCurrentHeader = () => getHeader(componentConfig, headerType);
  const header = getCurrentHeader();

  const isDyProductSource = isDy(
    componentConfig.productsSource === DyProductSource.DYR ||
      componentConfig.productsSource === DyProductSource.DYS
      ? componentConfig.productsSource
      : undefined,
  );

  useEffect(() => {
    if (isDyProductSource) {
      setHeaderType(DyHeaderOptions.UNKNOWN);
    }
  }, [componentConfig.productsSource, isDyProductSource]);

  return (
    <>
      {isDyProductSource && (
        <Grid item xs={12}>
          <HeaderWidgetButtonGroup
            value={headerType}
            exclusive
            onChange={handleHeaderCustomerTypeChange}>
            <HeaderWidgetButton value={DyHeaderOptions.UNKNOWN}>
              {lang.unknown}
            </HeaderWidgetButton>
            <HeaderWidgetButton value={DyHeaderOptions.KNOWN}>
              {lang.known}
            </HeaderWidgetButton>
          </HeaderWidgetButtonGroup>
        </Grid>
      )}

      <Grid item xs={12}>
        <InputTextLabel>{lang.header}</InputTextLabel>
      </Grid>

      <>
        <Grid item xs={12}>
          <SwitchButtonWidget
            value={headerKey?.visible}
            onChange={(value) => {
              handleOnChange(headerKeyString(), {
                ...header,
                visible: value,
                text: header?.text ?? '',
              });
            }}
          />
        </Grid>
        {headerKey?.visible && (
          <>
            <Grid item xs={12}>
              <RichTextEditor
                key={`${componentConfig.productsSource}-${headerType}`}
                placeholder={lang.headerText}
                value={headerKey.text}
                hide={hideToolbarItems}
                onChange={(value) =>
                  handleOnChange(
                    headerKeyString('text'),
                    convertSpacesToHTMLFormat(value as string),
                  )
                }
                enableSuperscriptToolbars={true}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectDropdownWidget
                label={lang.fontFamily}
                value={headerKey.font ?? theme?.config?.header?.font}
                onChange={(value) =>
                  handleOnChange(headerKeyString('font'), value)
                }
                selectOptions={mappedFonts}
                hasError={!!errorKey?.font}
                error={errorKey?.font?.message || false}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.fontSize}
                value={headerKey?.fontSize ?? theme?.config?.header?.fontSize}
                onChange={(value) =>
                  handleOnChange(headerKeyString('fontSize'), value)
                }
                options={{ suffix: 'px' }}
                hasError={!!errorKey?.fontSize}
                error={errorKey?.fontSize?.message || false}
              />
            </Grid>
            <Grid item xs={6} display="flex" alignItems="end">
              <ColorPickerWidget
                label={lang.fontColor}
                value={headerKey?.color ?? theme?.config?.header?.color}
                onChange={(value) =>
                  handleOnChange(headerKeyString('color'), value)
                }
                hasError={!!errorKey?.color}
                error={errorKey?.color?.message || false}
              />
              <TextAlignButtonsWidget
                value={headerKey?.alignment}
                onChange={(value) =>
                  handleOnChange(headerKeyString('alignment'), value)
                }
                hasError={!!errorKey?.alignment}
                error={errorKey?.alignment?.message || false}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.lineHeight}
                value={
                  headerKey?.lineHeight ?? theme?.config?.header?.lineHeight
                }
                onChange={(value) =>
                  handleOnChange(headerKeyString('lineHeight'), value)
                }
                options={{ suffix: 'px' }}
                hasError={!!errorKey?.lineHeight}
                error={errorKey?.lineHeight?.message || false}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.marginBelow}
                value={headerKey?.marginBottom}
                onChange={(value) =>
                  handleOnChange(headerKeyString('marginBottom'), value)
                }
                options={{ suffix: 'px' }}
                hasError={!!errorKey?.marginBottom}
                error={errorKey?.marginBottom?.message || false}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.marginTop}
                value={headerKey?.marginTop}
                onChange={(value) =>
                  handleOnChange(headerKeyString('marginTop'), value)
                }
                options={{ suffix: 'px' }}
                hasError={!!errorKey?.marginTop}
                error={errorKey?.marginTop?.message || false}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.height}
                value={headerKey?.height ?? theme?.config?.header?.height}
                onChange={(value) =>
                  handleOnChange(headerKeyString('height'), value)
                }
                hasError={!!errorKey?.height}
                error={errorKey?.height?.message || false}
              />
            </Grid>
          </>
        )}
      </>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <InputTextLabel
          status={
            isDyProductSource && InputStatus.DISABLED
              ? InputStatus.DISABLED
              : 'enabled'
          }>
          {lang.headerDescription}
        </InputTextLabel>
      </Grid>
      <Grid item xs={12}>
        <SwitchButtonWidget
          value={!isDyProductSource && headerKey?.description?.visible}
          onChange={(value) =>
            handleOnChange(headerKeyString('description.visible'), value)
          }
          disabled={isDyProductSource}
        />
      </Grid>

      {!isDyProductSource && headerKey?.description?.visible && (
        <>
          <Grid item xs={12}>
            <InputWidget
              label={lang.descriptionText}
              value={headerKey?.description?.text}
              onChange={(value) =>
                handleOnChange(headerKeyString('description.text'), value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <SelectDropdownWidget
              label={lang.fontFamily}
              value={
                headerKey?.description?.font ??
                theme?.config.headerDescription?.font
              }
              onChange={(value) => {
                handleOnChange(headerKeyString('description.font'), value);
              }}
              selectOptions={mappedFonts}
              hasError={!!errorKey?.description?.font}
              error={errorKey?.description?.font?.message || false}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={
                headerKey?.description?.fontSize ??
                theme?.config.headerDescription?.fontSize
              }
              onChange={(value) =>
                handleOnChange(headerKeyString('description.fontSize'), value)
              }
              options={{ suffix: 'px' }}
              hasError={!!errorKey?.description?.fontSize}
              error={errorKey?.description?.fontSize?.message || false}
            />
          </Grid>
          <Grid item xs={6} display="flex" alignItems="end">
            <ColorPickerWidget
              label={lang.fontColor}
              value={
                headerKey?.description?.color ??
                theme?.config.headerDescription?.color
              }
              onChange={(value) =>
                handleOnChange(headerKeyString('description.color'), value)
              }
              hasError={!!errorKey?.description?.color}
              error={errorKey?.description?.color?.message || false}
              colorPickerMode="block"
              options={{ triangle: 'hide' }}
              pickerWrapperStyle={{ left: '0' }}
            />
            <TextAlignButtonsWidget
              value={headerKey?.description?.alignment}
              onChange={(value) =>
                handleOnChange(headerKeyString('description.alignment'), value)
              }
              hasError={!!errorKey?.description?.alignment}
              error={errorKey?.description?.alignment?.message || false}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={
                headerKey?.description?.lineHeight ??
                theme?.config.headerDescription?.lineHeight
              }
              onChange={(value) =>
                handleOnChange(headerKeyString('description.lineHeight'), value)
              }
              options={{ suffix: 'px' }}
              hasError={!!errorKey?.description?.lineHeight}
              error={errorKey?.description?.lineHeight?.message || false}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={
                headerKey?.description?.height ??
                theme?.config.headerDescription?.height
              }
              onChange={(value) =>
                handleOnChange(headerKeyString('description.height'), value)
              }
              hasError={!!errorKey?.description?.height}
              error={errorKey?.description?.height?.message || false}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.width}
              value={headerKey?.description?.width}
              onChange={(value) =>
                handleOnChange(headerKeyString('description.width'), value)
              }
              hasError={!!errorKey?.description?.width}
              error={errorKey?.description?.width?.message || false}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.marginBelow}
              value={headerKey?.description?.marginBottom}
              onChange={(value) =>
                handleOnChange(
                  headerKeyString('description.marginBottom'),
                  value,
                )
              }
              options={{ suffix: 'px' }}
              hasError={!!errorKey?.description?.marginBottom}
              error={errorKey?.description?.marginBottom?.message || false}
            />
          </Grid>
          {!hidden?.includes(HeaderTextStylingOptions.HEADER_LAYOUT) && (
            <Grid item xs={6}>
              <SelectDropdownWidget
                label={lang.position}
                value={headerKey?.layout}
                hasError={!!errorKey?.layout}
                error={errorKey?.layout?.message || false}
                selectOptions={[
                  {
                    label: 'Horizontal',
                    value: HeaderLayout.LEFT_RIGHT,
                  },
                  {
                    label: 'Vertical',
                    value: HeaderLayout.TOP_BOTTOM,
                  },
                ]}
                onChange={(value) =>
                  handleOnChange(headerKeyString('layout'), value)
                }
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};
